import { useTranslation } from 'react-i18next'
import type { RemakeVariant } from '@/lib/api/amazon-listing.types'
import type { CompiledRules } from '@/hooks/validation.types'
import { EditableTitle } from './EditableTitle'
import { EditableBullet } from './EditableBullet'
import { EditableDescription } from './EditableDescription'

interface RemakeFieldGroupProps {
  variant: RemakeVariant
  variantIndex: number
  edits: Record<string, string | string[] | null | undefined>
  onEditChange: (vi: number, field: string, value: string | string[]) => void
  onResetField: (vi: number, field: string) => void
  rules: CompiledRules | undefined
}

export function RemakeFieldGroup({ variant, variantIndex, edits, onEditChange, onResetField, rules }: RemakeFieldGroupProps) {
  const { t } = useTranslation()
  const titleValue = (edits.title as string) ?? variant.title
  const descValue = (edits.description as string) ?? variant.description

  const bullets = variant.bullets ?? []

  const getBulletValue = (i: number): string => {
    const editedBullets = edits.bullets as (string | null)[] | undefined
    return editedBullets?.[i] ?? bullets[i] ?? ''
  }

  const handleBulletChange = (i: number, value: string) => {
    const current = bullets.map((_, idx) => getBulletValue(idx))
    current[i] = value
    onEditChange(variantIndex, 'bullets', current)
  }

  return (
    <div className="space-y-4">
      <EditableTitle
        value={titleValue}
        original={variant.title}
        onChange={(v) => onEditChange(variantIndex, 'title', v)}
        rules={rules?.title}
      />

      <div className="space-y-2">
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {t('remake.field.bullets')}
        </span>
        {bullets.map((bullet, i) => (
          <EditableBullet
            key={i}
            value={getBulletValue(i)}
            original={bullet}
            index={i}
            onChange={(v) => handleBulletChange(i, v)}
            onReset={() => onResetField(variantIndex, 'bullets')}
            rules={rules?.bullets}
          />
        ))}
      </div>

      <EditableDescription
        value={descValue}
        original={variant.description}
        onChange={(v) => onEditChange(variantIndex, 'description', v)}
        rules={rules?.description}
      />
    </div>
  )
}
